<template>
  <div class="prj-dash">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Project Dashboard</h5>
        <p class="page-sub">Portfolio overview — performance, budgets &amp; timelines at a glance</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="$router.push(routePrefix + '/projects')">
          <font-awesome-icon icon="list" class="me-1" /> All Projects
        </button>
        <button class="btn-icon-refresh" @click="load" :disabled="loading" title="Refresh">
          <font-awesome-icon icon="redo" />
        </button>
      </div>
    </div>

    <div v-if="loading" class="full-loader">
      <div class="spinner"></div><p>Loading dashboard…</p>
    </div>

    <template v-else>

      <!-- ── KPI Cards ─────────────────────────────────────────────── -->
      <div class="kpi-row">
        <div class="kpi-card kpi-card--blue">
          <div class="kpi-icon"><font-awesome-icon icon="diagram-project" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.status_counts?.active ?? 0 }}</span>
            <span class="kpi-label">Active Projects</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--teal">
          <div class="kpi-icon"><font-awesome-icon icon="money-bill-wave" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ formatMoney(stats.active_contract_value ?? 0) }}</span>
            <span class="kpi-label">Active Contract Value</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--purple">
          <div class="kpi-icon"><font-awesome-icon icon="clock" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.hours_this_month ?? 0 }}h</span>
            <span class="kpi-label">Hours This Month</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--amber">
          <div class="kpi-icon"><font-awesome-icon icon="file-invoice" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ formatMoney(stats.billing_pending ?? 0) }}</span>
            <span class="kpi-label">Pending Billing</span>
          </div>
        </div>
        <div class="kpi-card kpi-card--red">
          <div class="kpi-icon"><font-awesome-icon icon="hourglass-half" /></div>
          <div class="kpi-body">
            <span class="kpi-value">{{ stats.pending_approvals ?? 0 }}</span>
            <span class="kpi-label">Pending Timesheet Approvals</span>
          </div>
        </div>
      </div>

      <!-- ── Row 1: Status Donut + Type Bar ────────────────────────── -->
      <div class="chart-row">
        <div class="chart-card chart-card--sm">
          <div class="chart-card-header">
            <span class="chart-card-title">Projects by Status</span>
            <span class="chart-card-sub">{{ totalProjects }} total</span>
          </div>
          <div class="chart-wrap">
            <GChart
              v-if="statusChartData.length > 1"
              type="PieChart"
              :data="statusChartData"
              :options="donutOpts"
              style="width:100%;height:260px"
            />
            <div v-else class="chart-empty">No project data</div>
          </div>
          <div class="legend-row">
            <div v-for="(item, i) in statusLegend" :key="i" class="legend-item">
              <span class="legend-dot" :style="{ background: item.color }"></span>
              <span class="legend-label">{{ item.label }}</span>
              <span class="legend-val">{{ item.count }}</span>
            </div>
          </div>
        </div>

        <div class="chart-card chart-card--md">
          <div class="chart-card-header">
            <span class="chart-card-title">Projects by Type</span>
          </div>
          <div class="chart-wrap">
            <GChart
              v-if="typeChartData.length > 1"
              type="BarChart"
              :data="typeChartData"
              :options="typeBarOpts"
              style="width:100%;height:260px"
            />
            <div v-else class="chart-empty">No project data</div>
          </div>
        </div>

        <div class="chart-card chart-card--sm">
          <div class="chart-card-header">
            <span class="chart-card-title">Billing Overview</span>
          </div>
          <div class="chart-wrap">
            <GChart
              v-if="billingChartData.length > 1"
              type="PieChart"
              :data="billingChartData"
              :options="billingDonutOpts"
              style="width:100%;height:260px"
            />
            <div v-else class="chart-empty">No billing data</div>
          </div>
          <div class="billing-totals">
            <div class="billing-row">
              <span class="billing-dot billing-dot--amber"></span>
              <span class="billing-lbl">Pending</span>
              <span class="billing-amt">{{ formatMoney(stats.billing_pending ?? 0) }}</span>
            </div>
            <div class="billing-row">
              <span class="billing-dot billing-dot--green"></span>
              <span class="billing-lbl">Invoiced</span>
              <span class="billing-amt">{{ formatMoney(stats.billing_invoiced ?? 0) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Row 2: Monthly Hours + Budget vs Actual ────────────────── -->
      <div class="chart-row">
        <div class="chart-card chart-card--lg">
          <div class="chart-card-header">
            <span class="chart-card-title">Hours Logged – Last 6 Months</span>
          </div>
          <div class="chart-wrap">
            <GChart
              v-if="monthlyHoursData.length > 1"
              type="AreaChart"
              :data="monthlyHoursData"
              :options="hoursAreaOpts"
              style="width:100%;height:240px"
            />
            <div v-else class="chart-empty">No timesheet data</div>
          </div>
        </div>

        <div class="chart-card chart-card--lg">
          <div class="chart-card-header">
            <span class="chart-card-title">Budget vs Actual Cost</span>
            <span class="chart-card-sub">Active projects</span>
          </div>
          <div class="chart-wrap">
            <GChart
              v-if="budgetVsCostData.length > 1"
              type="ColumnChart"
              :data="budgetVsCostData"
              :options="budgetColOpts"
              style="width:100%;height:240px"
            />
            <div v-else class="chart-empty">No budget data yet</div>
          </div>
        </div>
      </div>

      <!-- ── Row 3: Due Soon + Pending Timesheets ───────────────────── -->
      <div class="table-row">
        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title"><font-awesome-icon icon="calendar-alt" class="me-1 icon-amber" />Projects Due Soon</span>
            <span class="table-card-sub">Next 30 days</span>
          </div>
          <div v-if="!dueSoon.length" class="table-empty">No projects ending in the next 30 days</div>
          <table v-else class="mini-table">
            <thead><tr><th>Code</th><th>Project</th><th>Due</th><th>Days Left</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="p in dueSoon" :key="p.id" class="mini-row" @click="goToProject(p.id)">
                <td class="mono">{{ p.project_code }}</td>
                <td>{{ p.project_name }}</td>
                <td>{{ formatDate(p.end_date) }}</td>
                <td>
                  <span :class="['days-pill', daysClass(p.end_date)]">
                    {{ daysLeft(p.end_date) }}d
                  </span>
                </td>
                <td><span :class="['status-badge', `status-badge--${p.status}`]">{{ p.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-card">
          <div class="table-card-header">
            <span class="table-card-title"><font-awesome-icon icon="clock" class="me-1 icon-blue" />Pending Timesheet Approvals</span>
            <span class="table-card-sub">Submitted, awaiting review</span>
          </div>
          <div v-if="!pendingTimesheets.length" class="table-empty">No timesheets awaiting approval</div>
          <table v-else class="mini-table">
            <thead><tr><th>Employee</th><th>Project</th><th>Week</th><th>Hours</th><th></th></tr></thead>
            <tbody>
              <tr v-for="t in pendingTimesheets" :key="t.id">
                <td>{{ t.employee ? t.employee.first_name + ' ' + t.employee.last_name : '—' }}</td>
                <td>
                  <span v-if="t.project" class="project-tag">
                    <span class="project-code-badge">{{ t.project.project_code }}</span>
                  </span>
                  <span v-else class="text-dim">—</span>
                </td>
                <td>{{ formatDate(t.week_start) }}</td>
                <td class="amount-col">{{ t.total_hours }}h</td>
                <td><button class="btn-link" @click="goToTimesheets">Review</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

const STATUS_COLORS = {
  active:    '#22c55e',
  draft:     '#94a3b8',
  on_hold:   '#f97316',
  closed:    '#64748b',
  cancelled: '#ef4444',
};

export default {
  name: 'ProjectDashboard',
  setup() { return { toast: useToast() }; },
  data() {
    return { loading: false, stats: {} };
  },
  computed: {
    routePrefix() {
      const code = this.$route.params.company_code;
      return code ? `/${code}` : '';
    },
    totalProjects() {
      return Object.values(this.stats.status_counts ?? {}).reduce((s, v) => s + v, 0);
    },
    statusLegend() {
      return Object.entries(this.stats.status_counts ?? {}).map(([key, count]) => ({
        label: key.replace('_', ' '),
        color: STATUS_COLORS[key] ?? '#94a3b8',
        count,
      }));
    },

    // ── Google Charts data arrays ──────────────────────────────────────
    statusChartData() {
      const rows = Object.entries(this.stats.status_counts ?? {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => [k.charAt(0).toUpperCase() + k.slice(1).replace('_', ' '), v]);
      return rows.length ? [['Status', 'Count'], ...rows] : [['Status', 'Count']];
    },
    typeChartData() {
      const rows = Object.entries(this.stats.type_counts ?? {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => [k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), v]);
      return rows.length ? [['Type', 'Projects'], ...rows] : [['Type', 'Projects']];
    },
    billingChartData() {
      const pending  = this.stats.billing_pending  ?? 0;
      const invoiced = this.stats.billing_invoiced ?? 0;
      if (!pending && !invoiced) return [['Status', 'Amount']];
      return [['Status', 'Amount'], ['Pending', pending], ['Invoiced', invoiced]];
    },
    monthlyHoursData() {
      const rows = (this.stats.monthly_hours ?? []).map(m => [m.month, m.hours]);
      return rows.length ? [['Month', 'Hours'], ...rows] : [['Month', 'Hours']];
    },
    budgetVsCostData() {
      const rows = (this.stats.budget_vs_cost ?? []).map(r => [r.project_code, r.budget, r.actual]);
      return rows.length ? [['Project', 'Budget', 'Actual'], ...rows] : [['Project', 'Budget', 'Actual']];
    },
    dueSoon()           { return this.stats.due_soon          ?? []; },
    pendingTimesheets() { return this.stats.pending_timesheets ?? []; },

    // ── Chart options ──────────────────────────────────────────────────
    donutOpts() {
      return {
        pieHole: 0.45,
        colors: Object.entries(this.stats.status_counts ?? {})
          .filter(([, v]) => v > 0)
          .map(([k]) => STATUS_COLORS[k] ?? '#94a3b8'),
        legend: { position: 'none' },
        chartArea: { width: '90%', height: '85%' },
        tooltip: { showColorCode: true },
        backgroundColor: 'transparent',
      };
    },
    typeBarOpts() {
      return {
        colors: ['#004AAD'],
        legend: { position: 'none' },
        chartArea: { width: '65%', height: '80%' },
        hAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, gridlines: { color: '#f0f4f8' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, format: '0' },
        backgroundColor: 'transparent',
        bar: { groupWidth: '55%' },
      };
    },
    billingDonutOpts() {
      return {
        pieHole: 0.45,
        colors: ['#f97316', '#22c55e'],
        legend: { position: 'none' },
        chartArea: { width: '90%', height: '85%' },
        tooltip: { showColorCode: true },
        backgroundColor: 'transparent',
      };
    },
    hoursAreaOpts() {
      return {
        colors: ['#004AAD'],
        legend: { position: 'none' },
        chartArea: { width: '88%', height: '80%' },
        hAxis: { textStyle: { fontSize: 11, color: '#7a90b0' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, gridlines: { color: '#f0f4f8' } },
        backgroundColor: 'transparent',
        areaOpacity: 0.12,
        pointSize: 5,
        lineWidth: 2,
      };
    },
    budgetColOpts() {
      return {
        colors: ['#004AAD', '#f97316'],
        legend: { position: 'top', textStyle: { fontSize: 11, color: '#7a90b0' } },
        chartArea: { width: '80%', height: '72%' },
        hAxis: { textStyle: { fontSize: 10, color: '#7a90b0' } },
        vAxis: { textStyle: { fontSize: 11, color: '#7a90b0' }, minValue: 0, gridlines: { color: '#f0f4f8' }, format: 'short' },
        backgroundColor: 'transparent',
        bar: { groupWidth: '65%' },
      };
    },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get('/projects/dashboard');
        this.stats = data.data ?? {};
      } catch { this.toast.error('Failed to load dashboard.'); }
      finally { this.loading = false; }
    },
    goToProject(id) { this.$router.push(this.routePrefix + '/projects/' + id); },
    goToTimesheets() { this.$router.push(this.routePrefix + '/timesheets'); },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    formatMoney(v) { return new Intl.NumberFormat('en-NG', { notation: 'compact', maximumFractionDigits: 1 }).format(v); },
    daysLeft(d) { return d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null; },
    daysClass(d) {
      const n = this.daysLeft(d);
      if (n === null) return '';
      if (n <= 7)  return 'days-pill--red';
      if (n <= 14) return 'days-pill--amber';
      return 'days-pill--green';
    },
  },
};
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.prj-dash { padding-bottom: 32px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.me-1 { margin-right:4px; }

/* ── Full loader ──────────────────────────────────────────────────── */
.full-loader { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:320px; gap:12px; color:#7a90b0; }

/* ── KPI row ─────────────────────────────────────────────────────── */
.kpi-row { display:grid; grid-template-columns:repeat(5, 1fr); gap:16px; margin-bottom:20px; }
.kpi-card { display:flex; align-items:center; gap:14px; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px 20px; position:relative; overflow:hidden; }
.kpi-card::before { content:''; position:absolute; top:0; left:0; width:4px; height:100%; border-radius:4px 0 0 4px; }
.kpi-card--blue::before   { background:#004AAD; }
.kpi-card--teal::before   { background:#0d9488; }
.kpi-card--purple::before { background:#7c3aed; }
.kpi-card--amber::before  { background:#f97316; }
.kpi-card--red::before    { background:#dc2626; }
.kpi-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.kpi-card--blue   .kpi-icon { background:#eff6ff; color:#004AAD; }
.kpi-card--teal   .kpi-icon { background:#f0fdfa; color:#0d9488; }
.kpi-card--purple .kpi-icon { background:#f5f3ff; color:#7c3aed; }
.kpi-card--amber  .kpi-icon { background:#fff7ed; color:#f97316; }
.kpi-card--red    .kpi-icon { background:#fef2f2; color:#dc2626; }
.kpi-body { min-width:0; }
.kpi-value { display:block; font-size:22px; font-weight:800; color:#1a2b45; line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.kpi-label { display:block; font-size:11.5px; color:#7a90b0; margin-top:3px; }

/* ── Chart rows ──────────────────────────────────────────────────── */
.chart-row { display:grid; grid-template-columns:1fr 1.4fr 1fr; gap:16px; margin-bottom:20px; }
.chart-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.chart-card--lg { grid-column:span 1; }
.chart-card-header { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:12px; }
.chart-card-title { font-size:14px; font-weight:700; color:#1a2b45; }
.chart-card-sub   { font-size:12px; color:#7a90b0; }
.chart-wrap { width:100%; }
.chart-empty { display:flex; align-items:center; justify-content:center; height:200px; color:#aab8cc; font-size:13px; }

/* Legend */
.legend-row { display:flex; flex-wrap:wrap; gap:8px 16px; margin-top:12px; }
.legend-item { display:flex; align-items:center; gap:6px; }
.legend-dot  { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.legend-label { font-size:12px; color:#7a90b0; text-transform:capitalize; }
.legend-val   { font-size:12px; font-weight:700; color:#1a2b45; }

/* Billing summary */
.billing-totals { margin-top:12px; display:flex; flex-direction:column; gap:6px; }
.billing-row { display:flex; align-items:center; gap:8px; }
.billing-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.billing-dot--amber { background:#f97316; }
.billing-dot--green { background:#22c55e; }
.billing-lbl { font-size:12px; color:#7a90b0; flex:1; }
.billing-amt { font-size:13px; font-weight:700; color:#1a2b45; }

/* ── Second chart row (2 cols) ──────────────────────────────────── */
.chart-row:nth-child(3) { grid-template-columns:1fr 1fr; }

/* ── Table row ───────────────────────────────────────────────────── */
.table-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.table-card-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; }
.table-card-title { font-size:14px; font-weight:700; color:#1a2b45; }
.table-card-sub   { font-size:12px; color:#7a90b0; }
.table-empty { padding:32px 20px; text-align:center; color:#aab8cc; font-size:13px; }
.mini-table { width:100%; border-collapse:collapse; }
.mini-table th { padding:9px 16px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.mini-table td { padding:11px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.mini-table tbody tr:last-child td { border-bottom:none; }
.mini-row { cursor:pointer; }
.mini-row:hover td { background:#f8fafc; }
.mono { font-family:monospace; font-size:12px; color:#004AAD; font-weight:600; }
.amount-col { text-align:right; font-variant-numeric:tabular-nums; }

/* Days pill */
.days-pill { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:2px 9px; }
.days-pill--green { background:#dcfce7; color:#15803d; }
.days-pill--amber { background:#fff7ed; color:#c2410c; }
.days-pill--red   { background:#fee2e2; color:#dc2626; }

/* Status badges */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--active    { background:#dcfce7; color:#15803d; }
.status-badge--on_hold   { background:#fff7ed; color:#c2410c; }
.status-badge--closed    { background:#f1f5f9; color:#475569; }
.status-badge--cancelled { background:#fee2e2; color:#dc2626; }

/* Project tag */
.project-tag { display:inline-flex; align-items:center; gap:5px; }
.project-code-badge { font-family:monospace; font-size:11px; font-weight:700; color:#004AAD; background:#eff6ff; padding:2px 7px; border-radius:5px; }
.text-dim { color:#7a90b0; }

/* Icon tints */
.icon-amber { color:#f97316; }
.icon-blue  { color:#004AAD; }

/* ── Buttons ──────────────────────────────────────────────────────── */
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-icon-refresh { width:38px; height:38px; border:1.5px solid #dde5f0; border-radius:8px; background:#fff; color:#7a90b0; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:14px; }
.btn-icon-refresh:hover:not(:disabled) { border-color:#004AAD; color:#004AAD; }
.btn-icon-refresh:disabled { opacity:.5; cursor:not-allowed; }
.btn-link { background:none; border:none; font-size:12.5px; font-weight:600; color:#004AAD; cursor:pointer; padding:4px 6px; border-radius:5px; }
.btn-link:hover { background:#eff6ff; }

/* ── Spinner ──────────────────────────────────────────────────────── */
.spinner { width:36px; height:36px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width:1200px) {
  .kpi-row { grid-template-columns:repeat(3, 1fr); }
  .chart-row { grid-template-columns:1fr 1fr; }
  .chart-row:first-of-type .chart-card:nth-child(3) { grid-column:span 2; }
}
@media (max-width:768px) {
  .kpi-row  { grid-template-columns:1fr 1fr; }
  .chart-row, .chart-row:nth-child(3), .table-row { grid-template-columns:1fr; }
}
</style>
